<script lang="ts" setup>
import type { ImageProps } from '~/types/images'
import type { UIProp } from '~/types/elementsUi'

defineOptions({
  inheritAttrs: false
})

interface VModalImageProps {
  image: ImageProps
  ui?: UIProp<'figure' | 'image'>
}

const props = defineProps<VModalImageProps>()

const localeStore = useLocaleStore()

const isOpen = ref(false)

const viewport = useViewport()

const openModal = () => {
  isOpen.value = true
}

const closeModal = () => {
  isOpen.value = false
}

watch(
  () => viewport.isLessThan('tablet'),
  () => (isOpen.value = false)
)

const computedUi = computed(() =>
  nuxtUiMerge(props.ui, {
    figure: tw`group relative aspect-4/3 overflow-hidden rounded-2xl shadow-md transition-shadow duration-300 hover:shadow-xl md:cursor-pointer`,
    image: tw`absolute inset-0 h-full w-full transition-transform duration-300 group-hover:scale-[1.03]`
  })
)

const computedDescription = computed(() => props.image.alt[localeStore.locale])
</script>

<template>
  <UModal
    v-model:open="isOpen"
    :ui="{ content: 'max-w-6xl' }"
    :title="computedDescription"
    :description="computedDescription"
  >
    <template #content>
      <div class="flex flex-col-reverse md:flex-col">
        <div class="flex w-full items-center justify-between md:justify-end">
          <VLink :href="image.src" download target="_blank">
            <VIcon icon="fa-save" color="gray" class="p-6" size="medium" />
          </VLink>

          <VIcon icon="fa-x" color="gray" class="p-6" size="medium" @click="closeModal" />
        </div>
        <figure class="relative inline-block h-full">
          <VImage
            :image="image"
            class="h-full w-full object-cover"
            sizes="xl:1152px 100vw"
          />
        </figure>
      </div>
    </template>
  </UModal>

  <figure
    :class="computedUi?.figure"
    tabindex="0"
    :aria-label="computedDescription"
    role="button"
    v-bind="$attrs"
    @click="openModal"
    @keydown.enter.prevent="openModal"
    @keydown.space.prevent="openModal"
  >
    <VImage :image="image" :class="computedUi?.image" sizes="xxl:1408px 100vw" />

    <div
      class="pointer-events-none absolute inset-0 hidden items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/30 md:flex"
    >
      <span
        class="text-sm font-semibold tracking-wide text-primary-text opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      >
        <VIcon icon="fa-plus" size="large" />
      </span>
    </div>
  </figure>
</template>

<style scoped></style>
